import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchAllWorkbooks,
  deleteWorkbook,
  updateWorkbook,
  restoreWorkbook,
  archiveWorkbook,
  deleteAllWorkbooks,
} from 'containers/SuperAdmin/redux/actions/workbook';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import Loader from '../../../../components/Loader';
import EnhancedTable from '../../components/Table';
import PageHeader from '../../components/PageHeader';
import DeleteDialog from 'components/DeleteDialog';
import { Button, IconButton } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import RestoreFromTrashIcon from '@material-ui/icons/RestoreFromTrash';
import ArchiveIcon from '@material-ui/icons/Archive';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import InputField from 'containers/SuperAdmin/components/InputField';
import FilterBar from '../../components/FilterBar';
import { ImagesDialog } from 'containers/SuperAdmin/components/ImagesDialog';
import SettingDialog from 'containers/SuperAdmin/components/SettingDialog';

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
  },
}));

export default function Workbooks({ titleName, id }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { workbooks, fetching, actionLoading } = useSelector(
    ({ admin }) => admin.workbook
  );

  const [view, setView] = useState('normal');
  const [searchString, setSearchString] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [nameId, setNameId] = useState(null);
  const [name, setName] = useState(null);
  const [workbookIdImages, setWorkbookIdImages] = useState(null);
  const [deleteAllDialog, setDeleteAllDialog] = useState(false);
  const [content, setContent] = useState([]);

  const [sort, setSort] = useState({ orderBy: 'createdAt', orderDir: 'desc' });

  const loadPage = (page, sortOptions) => {
    let update = {
      page,
      limit: workbooks.limit,
      ...sortOptions,
    };
    if (searchString !== '') {
      update.search = searchString;
    }
    if (view === 'archived') {
      update = { ...update, isArchived: true };
    }
    dispatch(fetchAllWorkbooks(update));
    setSort(sortOptions);
  };

  const headCells = [
    {
      id: 'title',
      label: 'Title',
      formatter: (r) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {nameId === r._id ? (
            <InputField
              initialValue={r.title}
              field={name}
              setField={setName}
              onCancel={() => {
                setName(null);
                setNameId(null);
              }}
              onSave={(d) => dispatch(updateWorkbook(r._id, { title: d }))}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginLeft: 15 }}>{r.title}</span>
              <IconButton
                onClick={() => {
                  setName(r.title);
                  setNameId(r._id);
                }}
                size='small'
              >
                <EditIcon fontSize={'small'} />
              </IconButton>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'content',
      label: 'Workbook Pages',
      formatter: (r) => (
        <div
          style={{ display: 'flex', alignItems: 'center' }}
          onClick={() => {
            setContent(r.content);
            setWorkbookIdImages(r._id);
          }}
        >
          {r.content[0] && (
            <img
              src={r.content[0]}
              style={{ width: 'auto', height: 32, objectFit: 'contain' }}
              alt={r._id + 'content'}
            />
          )}
        </div>
      ),
    },
    {
      id: 'contentLength',
      label: 'Total Pages',
      formatter: (r) => r.content.length,
    },
    {
      id: 'actions',
      label: 'Actions',
      actions:
        view === 'normal'
          ? [
              {
                label: 'Edit',
                action: (r) => {
                  history.push(`/superadmin/workbooks/edit/${r._id}`);
                },
                icon: <EditIcon />,
                disabled: () => false,
              },
              {
                label: 'Archive',
                action: (r) => {
                  setDeleteId(r._id);
                  setOpenDeleteDialog(true);
                },
                icon: <ArchiveIcon />,
                disabled: () => false,
              },
            ]
          : [
              {
                label: 'Restore',
                action: (r) => {
                  dispatch(restoreWorkbook(r._id));
                },
                icon: <RestoreFromTrashIcon />,
                disabled: () => false,
              },
              {
                label: 'Permanently Delete',
                action: (r) => {
                  setDeleteId(r._id);
                  setOpenDeleteDialog(true);
                },
                icon: <DeleteForeverIcon />,
                disabled: () => false,
              },
            ],
    },
  ];

  useEffect(() => {
    dispatch(fetchAllWorkbooks());
  }, [dispatch]);

  const handleSearch = (d) => {
    setSearchString(d);
    let update = {
      page: workbooks.page,
      limit: workbooks.limit,
      search: d,
    };
    if (view === 'archived') {
      update = { ...update, isArchived: true };
    }
    dispatch(fetchAllWorkbooks(update));
  };

  const handleAction = () => {
    if (view === 'normal') {
      dispatch(fetchAllWorkbooks({ isArchived: true }));
    } else {
      dispatch(
        fetchAllWorkbooks({
          page: workbooks.page,
          limit: workbooks.limit,
        })
      );
    }
    setView(view === 'normal' ? 'archived' : 'normal');
  };

  return (
    <div className={classes.root}>
      <Loader fetching={fetching || actionLoading} />
      <PageHeader
        disableSearch
        title={titleName}
        options={
          <>
            <SettingDialog id={id} name={titleName} />
            <Button
              onClick={() => setDeleteAllDialog(true)}
              variant='contained'
              color='secondary'
            >
              Delete All
            </Button>
            <IconButton
              onClick={() => {
                history.push('/superadmin/workbooks/new');
              }}
            >
              <AddIcon />
            </IconButton>
          </>
        }
      />
      <FilterBar
        toggle={view === 'archived'}
        onSearch={handleSearch}
        enableTopics={false}
        onAction={handleAction}
      />
      {workbooks && workbooks.docs.length > 0 && (
        <Grid container direction='row' justifyContent='flex-start' spacing={2}>
          <Grid item sm={12} xs={12}>
            <EnhancedTable
              data={{
                ...workbooks,
                docs: workbooks.docs.filter((x) =>
                  view === 'normal' ? !x.isArchived : x.isArchived
                ),
                totalDocs: workbooks.docs.filter((x) =>
                  view === 'normal' ? !x.isArchived : x.isArchived
                ).length,
              }}
              headCells={headCells}
              orderField={sort.orderBy}
              orderDirection={sort.orderDir}
              handlePage={loadPage}
              handleOrder={(d) => loadPage(workbooks.page, d)}
            />
          </Grid>
        </Grid>
      )}
      <DeleteDialog
        open={openDeleteDialog}
        handleClose={() => setOpenDeleteDialog(false)}
        content={
          view === 'normal'
            ? 'This action will archive this workbook?'
            : 'This action will permanently delete this workbook?'
        }
        handleConfirm={() => {
          if (view === 'archived') {
            dispatch(deleteWorkbook(deleteId));
          } else {
            dispatch(archiveWorkbook(deleteId));
          }
          setDeleteId(null);
          setOpenDeleteDialog(false);
        }}
      />
      <DeleteDialog
        open={deleteAllDialog}
        handleClose={() => setDeleteAllDialog(false)}
        handleConfirm={() => {
          dispatch(deleteAllWorkbooks());
          setDeleteAllDialog(false);
        }}
      />
      <ImagesDialog
        title={'Upload Workbook Images'}
        editable
        open={Boolean(workbookIdImages)}
        initial={content}
        onClose={() => setWorkbookIdImages(null)}
        onResult={(d) => {
          dispatch(updateWorkbook(workbookIdImages, { content: d }));
          setWorkbookIdImages(null);
          setContent([]);
        }}
      />
    </div>
  );
}
